<script setup lang="ts">
const { staticUrl } = useImageUrl();

interface Review {
  id?: string | number;
  customer?: string;
  customer_name?: string;
  score: number;
  title?: string;
  comment?: string;
  product_name?: string;
  product?: string;
  company_logo?: string;
  image?: string;
  [key: string]: unknown;
}

interface Props {
  reviews: Review[] | Record<string, unknown>[];
}

defineProps<Props>();

const expandedReviews = ref<Set<number>>(new Set());

function getStars(score: number) {
  return Math.round(score);
}

function toggleDescription(index: number) {
  if (expandedReviews.value.has(index)) {
    expandedReviews.value.delete(index);
  } else {
    expandedReviews.value.add(index);
  }
}
</script>

<template>
  <div class="review-list">
    <div
      v-for="(review, i) in reviews"
      :key="(review as Review).id || i"
      class="review-card"
    >
      <div class="review-card__header">
        <!-- <span class="review-card__name">
          {{ (review as Review).customer_name || (review as Review).customer || "Anonymous" }}
        </span> -->
        <div class="review-card__stars">
          <fa
            v-for="s in 5"
            :key="s"
            icon="star"
            :class="['review-card__star', { filled: s <= getStars((review as Review).score) }]"
          />
        </div>
      </div>
      <p
        v-if="(review as Review).title"
        class="review-card__title"
      >
        {{ (review as Review).title }}
      </p>
      <p
        v-if="(review as Review).comment"
        class="review-card__comment"
        :class="{ shortened: !expandedReviews.has(i) }"
      >
        {{ (review as Review).comment }}
      </p>
      <button
        v-if="(review as Review).comment"
        type="button"
        class="review-card__toggle"
        @click.stop="toggleDescription(i)"
      >
        {{ expandedReviews.has(i) ? "Read less about this review" : "Read more about this review" }}
      </button>
      <div class="review-card__footer">
        <span
          v-if="(review as Review).product_name || (review as Review).product"
          class="review-card__product"
        >
          {{ (review as Review).product_name || (review as Review).product }}
        </span>
        <img
          v-if="(review as Review).image || (review as Review).company_logo"
          loading="lazy"
          class="review-card__logo"
          :src="
            staticUrl(
              `/Images/company-logos/${(review as Review).image || (review as Review).company_logo}-logo.svg`,
            )
          "
          :alt="(review as Review).image || (review as Review).company_logo"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.review-list {
  width: 100%;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1.5rem;
  @media (max-width: $tablet) {
    flex-direction: column;
    padding: 0;
    // Was 80% — testimonials inside the homepage carousel ended up
    // narrower than the carousel viewport, leaving awkward gutters
    // around each card on mobile. Full-width fills the slide cleanly.
    width: 100%;
  }
}

.review-card {
  cursor: pointer;
  border-radius: 0.4rem;
  transition: transform 0.2s;
  padding: 20px;
  // Border-box so the 20px padding stays inside the 100% width —
  // without this the card was 100% slide width PLUS 40px padding,
  // which pushed it 40px past the carousel viewport edge.
  box-sizing: border-box;
  background-color: var(--color-grey-4);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  &:hover {
    transform: scale(1.02);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__name {
    margin: 0;
    font-weight: 600;
  }

  &__stars {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  &__star {
    font-size: 15px;
    color: var(--color-grey-3);
    &.filled {
      color: var(--color-pink-1);
    }
  }

  &__title {
    margin: 10px 0;
    font-weight: 600;
  }

  &__comment {
    margin-top: 10px;
    flex: 1;
    word-wrap: break-word;
    font-family: inherit;

    &.shortened {
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      line-clamp: 3;
      -webkit-box-orient: vertical;
      max-height: 3.5rem;
    }
  }

  &__toggle {
    appearance: none;
    background: none;
    border: 0;
    padding: 0;
    margin-top: 0.25rem;
    cursor: pointer;
    text-decoration: none;
    font: inherit;
    font-size: 0.9rem;
    display: inline-block;
    color: var(--color-grey-1);
    text-align: left;

    &:hover {
      color: var(--color-pink-1);
      text-decoration: underline;
    }
  }

  &__footer {
    padding-top: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  &__product {
    font-size: 0.9rem;
    color: var(--color-grey-1);
  }

  &__logo {
    max-width: 100px;
    max-height: 40px;
    object-fit: contain;
  }
}
</style>
